import { CUBE_AGGREGATE_INTERACTION } from '@/api/hcp/interaction/types';
import { ChannelFocusKeys } from '@/api/queries/channelFocus/cacheKeys';
import { useGetUniqueCustomersReached } from '@/api/queries/engagement-view/useGetUniqueCustomersReached';
import { useGetHcpTotalCount } from '@/api/queries/hcp/useGetHcpTotalCount';
import useQuery from '@/api/queries/useQuery';
import { useInteractionChannelFilters } from '@/context/ChanelFocus/AdditionalFiltersContext';
import { Channel } from '@/models';
import { CubeQuery } from '@/types/cube';
import {
  getValueFromTimeComparisonResult,
  useGetAdditionalFilter,
  useGetDateRangeTimeDimensionComparison,
  useGetGlobalFilters,
} from '@/utils/cube';
import { Filter } from '@cubejs-client/core';
import { useMemo } from 'react';

type ChannelCoverageQueryResult = {
  [key: string]: string | number;
};

export interface ChannelCoverage {
  isLoading: boolean;
  current: number | undefined;
  previous: number | undefined;
}

const useChannelCoverageData = (
  measure: string,
  currentChannel: Channel,
  additionalFilters?: Filter,
): ChannelCoverage => {
  const { getFiltersForChannel } = useInteractionChannelFilters();
  const channelFilters = getFiltersForChannel(currentChannel);

  const combinedFilters = useGetAdditionalFilter(channelFilters, currentChannel);
  const globalFilters = useGetGlobalFilters();
  const dateRangeForEvent = useGetDateRangeTimeDimensionComparison(`${CUBE_AGGREGATE_INTERACTION}.interaction_date`);
  const { data: noUniqueHCPs, isLoading: isLoadingHCPCount } = useGetHcpTotalCount();

  // Determine applicable filters
  const appliedFilters = useMemo<Filter[]>(() => {
    return [globalFilters, ...(additionalFilters ? [additionalFilters] : []), combinedFilters];
  }, [globalFilters, additionalFilters, combinedFilters]);

  const query: CubeQuery = useMemo(
    () => ({
      limit: 1,
      filters: appliedFilters,
      measures: [measure],
      timeDimensions: [dateRangeForEvent],
    }),
    [appliedFilters, measure, dateRangeForEvent],
  );

  const {
    data: resultSet,
    isLoading,
    error,
  } = useQuery<ChannelCoverageQueryResult[]>({
    payload: query,
    options: {
      queryKey: [...ChannelFocusKeys.coverage(query)],
      enabled: true,
    },
  });

  const pivotData = error ? undefined : resultSet?.[0];
  const comparison = pivotData ? getValueFromTimeComparisonResult(pivotData, dateRangeForEvent, measure) : undefined;

  return {
    isLoading: isLoading || isLoadingHCPCount,
    current: noUniqueHCPs && comparison ? (comparison.current ?? 0) / noUniqueHCPs : undefined,
    previous: noUniqueHCPs && comparison ? (comparison.previous ?? 0) / noUniqueHCPs : undefined,
  };
};

export function useAllChannelTabData(): ChannelCoverage {
  const { data: { hcpsContacted, overallCoverage } = {}, isLoading } = useGetUniqueCustomersReached();

  if (!overallCoverage) {
    return { isLoading, previous: undefined, current: undefined };
  }

  const current = overallCoverage.currentValue * 0.01;
  const delta = hcpsContacted?.changePercentage;
  const projectedPrevious = typeof delta === 'number' ? current / (1 + delta) : NaN;
  // previousCoverage is a whole percent, so it needs the same scaling as current.
  const reportedPrevious =
    hcpsContacted?.previousCoverage === undefined ? undefined : hcpsContacted.previousCoverage * 0.01;
  // Zero coverage is a real value, so only a non-finite projection falls back to the reported previous coverage
  const previous = Number.isFinite(projectedPrevious) ? projectedPrevious : reportedPrevious;

  return { isLoading, previous, current };
}

export function useFaceToFaceData() {
  return useChannelCoverageData(
    `${CUBE_AGGREGATE_INTERACTION}.countCompletedFaceToFaceDistinctByHCP`,
    Channel.VISITS_1_TO_1,
  );
}

export function useEmailData() {
  return useChannelCoverageData(`${CUBE_AGGREGATE_INTERACTION}.countEmailDistinctByHCP`, Channel.EMAIL);
}

export function useEventData() {
  return useChannelCoverageData(`${CUBE_AGGREGATE_INTERACTION}.numberOfUniqueParticipants`, Channel.EVENT);
}

export function useWebsiteData() {
  return useChannelCoverageData(`${CUBE_AGGREGATE_INTERACTION}.countWebsiteDistinctByHCP`, Channel.WEBSITE, {
    member: `${CUBE_AGGREGATE_INTERACTION}.is_website_visit`,
    operator: 'equals',
    values: ['true'],
  });
}
